import React from 'react';
import { useTheme } from '../../context/ThemeContext';

// Our simple SkeletonLoader
export const SkeletonLoader = ({ className = '' }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div 
      className={`animate-pulse ${className} ${
        isDarkMode ? 'bg-white/5' : 'bg-base-200'
      }`}
    />
  );
};

// Simple stat card skeleton
export const SkeletonStatCard = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div className={`rounded-xl shadow ${
      isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'card bg-base-100'
    }`}>
      <div className="p-5">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${
            isDarkMode ? 'bg-blue-500/10' : 'bg-primary/10'
          }`}>
            <SkeletonLoader className="h-6 w-6 rounded" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt>
                <SkeletonLoader className="h-4 w-24 rounded mb-1" />
              </dt>
              <dd>
                <SkeletonLoader className="h-5 w-12 rounded" />
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

// Example studio skeleton (unchanged)
export const SkeletonStudioCard = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div className={`rounded-lg w-full ${
      isDarkMode 
        ? 'bg-[#1a1f2d]/90 border border-white/5' 
        : 'bg-base-100 shadow-lg'
    } p-5 flex flex-col h-[180px] relative transition-all duration-200`}>
      {/* Studio Name and Actions */}
      <div className="flex justify-between items-start mb-4">
        <div className="space-y-2">
          <SkeletonLoader className="h-[20px] w-48 rounded" />
          <SkeletonLoader className="h-[16px] w-32 rounded" />
        </div>
        <div className="flex space-x-2">
          <SkeletonLoader className="h-[18px] w-[18px] rounded-lg" />
          <SkeletonLoader className="h-[18px] w-[18px] rounded-lg" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="flex items-center">
          <SkeletonLoader className="h-[14px] w-[14px] mr-2 rounded" />
          <SkeletonLoader className="h-[14px] w-20 rounded" />
        </div>
        <div className="flex items-center">
          <SkeletonLoader className="h-[14px] w-[14px] mr-2 rounded" />
          <SkeletonLoader className="h-[14px] w-16 rounded" />
        </div>
      </div>

      {/* Address */}
      <div className="mt-auto">
        <div className="flex items-center">
          <SkeletonLoader className="h-[14px] w-[14px] mr-2 rounded" />
          <SkeletonLoader className="h-[14px] w-48 rounded" />
        </div>
      </div>
    </div>
  );
};

// Skeleton for the routine form modal
export const SkeletonRoutineFormModal = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div className={`relative w-full max-w-5xl ${
      isDarkMode
        ? "bg-[#1a1f2d]/95 backdrop-blur-xl border border-white/5"
        : "bg-white"
    } rounded-2xl overflow-hidden`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-8 py-6 ${
        isDarkMode ? "border-b border-white/5" : "border-b border-gray-100"
      }`}>
        <div className="flex items-center gap-6">
          <SkeletonLoader className="h-7 w-32 rounded-lg" />
          <SkeletonLoader className="h-8 w-48 rounded-full" />
        </div>
      </div>

      {/* Content Area */}
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="col-span-2 space-y-6">
            {/* Title Input */}
            <div className="space-y-2">
              <SkeletonLoader className="h-5 w-24 rounded" />
              <SkeletonLoader className="h-10 w-full rounded-xl" />
            </div>

            {/* Dancers Table */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <SkeletonLoader className="h-5 w-32 rounded" />
                <SkeletonLoader className="h-9 w-64 rounded-lg" />
              </div>

              <div className={`rounded-xl border overflow-hidden ${
                isDarkMode ? "border-white/10" : "border-gray-200"
              }`}>
                <div className="space-y-1">
                  {/* Table Header */}
                  <div className={`p-3 ${
                    isDarkMode ? "bg-[#252D42]" : "bg-gray-50"
                  }`}>
                    <div className="grid grid-cols-4 gap-4">
                      <SkeletonLoader className="h-4 w-12 rounded" />
                      <SkeletonLoader className="h-4 w-24 rounded" />
                      <SkeletonLoader className="h-4 w-16 rounded" />
                      <SkeletonLoader className="h-4 w-12 rounded" />
                    </div>
                  </div>
                  {/* Table Rows */}
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className={`p-3 ${
                      isDarkMode ? "border-t border-white/5" : "border-t border-gray-100"
                    }`}>
                      <div className="grid grid-cols-4 gap-4">
                        <SkeletonLoader className="h-8 w-8 rounded-full" />
                        <SkeletonLoader className="h-4 w-32 rounded" />
                        <SkeletonLoader className="h-4 w-20 rounded" />
                        <SkeletonLoader className="h-4 w-16 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Audio Section */}
            <div className="space-y-2">
              <SkeletonLoader className="h-5 w-24 rounded" />
              <div className={`rounded-xl overflow-hidden ${
                isDarkMode ? "bg-[#1a1f2d] border border-white/10" : "bg-gray-50 border border-gray-200"
              }`}>
                <div className="p-4">
                  <div className="flex items-center gap-4">
                    <SkeletonLoader className="h-10 w-10 rounded-full" />
                    <div className="flex-1">
                      <SkeletonLoader className="h-1.5 w-full rounded-full" />
                      <div className="flex justify-between mt-1.5">
                        <SkeletonLoader className="h-3 w-10 rounded" />
                        <SkeletonLoader className="h-3 w-10 rounded" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Selected Dancers */}
          <div className="col-span-1">
            <div className={`rounded-xl border h-full ${
              isDarkMode ? "border-white/10" : "border-gray-200"
            }`}>
              <div className={`p-4 border-b ${
                isDarkMode ? "border-white/10" : "border-gray-100"
              }`}>
                <SkeletonLoader className="h-5 w-32 rounded" />
                <SkeletonLoader className="h-4 w-24 rounded mt-1" />
              </div>
              <div className="p-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-2">
                    <div className="flex items-center gap-3">
                      <SkeletonLoader className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <SkeletonLoader className="h-4 w-24 rounded" />
                        <SkeletonLoader className="h-3 w-16 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`flex items-center justify-end px-8 py-6 gap-3 ${
        isDarkMode ? "border-t border-white/5" : "border-t border-gray-100"
      }`}>
        <SkeletonLoader className="h-9 w-20 rounded-lg" />
        <SkeletonLoader className="h-9 w-32 rounded-lg" />
      </div>
    </div>
  );
};

// UPDATED SkeletonRoutineCard to match new layout
export const SkeletonRoutineCard = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div
      className={`rounded-xl w-full ${
        isDarkMode 
          ? 'bg-[#1a1f2d]/90 border border-white/5 hover:border-white/10' 
          : 'bg-base-100 border border-gray-200'
      } p-5 flex flex-col h-[220px] relative transition-all duration-200`}
    >
      {/* Selection Checkbox */}
      <div className="absolute top-3 left-3">
        <SkeletonLoader className="h-5 w-5 rounded-md" />
      </div>

      {/* Title and Actions */}
      <div className="flex justify-between items-start mb-4 pl-8">
        <SkeletonLoader className="h-5 w-48 rounded" />
        <div className="flex space-x-2">
          <SkeletonLoader className="h-[18px] w-[18px] rounded-lg" />
          <SkeletonLoader className="h-[18px] w-[18px] rounded-lg" />
        </div>
      </div>

      {/* Dancer Avatars */}
      <div className="flex -space-x-2 mb-4">
        {[...Array(3)].map((_, i) => (
          <SkeletonLoader 
            key={i}
            className={`w-9 h-9 rounded-full border-2 ${
              isDarkMode ? 'border-[#1a1f2d]' : 'border-white'
            }`}
          />
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-y-3 gap-x-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center space-x-2">
            <SkeletonLoader className="h-4 w-4 rounded" />
            <SkeletonLoader className="h-4 w-20 rounded" />
          </div>
        ))}
      </div>

      {/* Next Event */}
      <div className="mt-auto pt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <SkeletonLoader className="h-4 w-16 rounded" />
            <SkeletonLoader className="h-4 w-24 rounded" />
          </div>
          <SkeletonLoader className="h-4 w-20 rounded" />
        </div>
      </div>
    </div>
  );
};

// Skeleton for events page card
export const SkeletonEventCard = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div
      className={`flex p-3 rounded-lg transition-colors duration-200 ${
        isDarkMode
          ? "bg-gray-800/50"
          : "bg-base-200"
      }`}
    >
      <div className="flex-shrink-0 flex justify-center items-center w-14 h-14 mr-3">
        <SkeletonLoader className="h-10 w-10 rounded-full" />
      </div>
      <div className="flex-1">
        <div className="flex justify-between">
          <SkeletonLoader className="h-5 w-32 rounded mb-1" />
          <SkeletonLoader className="h-4 w-20 rounded" />
        </div>
        <div className="mt-1 space-y-1.5">
          <div className="flex items-center">
            <SkeletonLoader className="h-4 w-4 rounded mr-2" />
            <SkeletonLoader className="h-4 w-28 rounded" />
          </div>
          <div className="flex items-center">
            <SkeletonLoader className="h-4 w-4 rounded mr-2" />
            <SkeletonLoader className="h-4 w-24 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};

// More detailed studio page skeleton
export const SkeletonStudioPageContent = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
        <div>
          <SkeletonLoader className="h-8 w-56 rounded mb-2" />
          <SkeletonLoader className="h-5 w-80 rounded" />
        </div>
        <div>
          <SkeletonLoader className="h-10 w-40 rounded" />
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className={`rounded-xl shadow p-5 ${
              isDarkMode
                ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
                : "card bg-base-100"
            }`}
          >
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-primary/10">
                <SkeletonLoader className="h-6 w-6 rounded" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <SkeletonLoader className="h-4 w-24 rounded mb-2" />
                <SkeletonLoader className="h-6 w-8 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Member Section */}
      <div
        className={`rounded-xl shadow p-4 ${
          isDarkMode
            ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
            : "card bg-base-100"
        }`}
      >
        <div className="mb-3">
          <div className="flex items-center gap-3 mb-2">
            <SkeletonLoader className="h-6 w-40 rounded" />
            <SkeletonLoader className="h-4 w-32 rounded" />
          </div>
          <SkeletonLoader className="h-4 w-96 rounded" />
        </div>
        
        {/* Form skeleton */}
        <div className="space-y-3">
          <div className="grid gap-2 items-end" style={{gridTemplateColumns: "auto 1fr 1fr 1fr 1fr 1fr 1fr auto"}}>
          <SkeletonLoader className="h-12 w-12 rounded-full" />
          <div>
            <SkeletonLoader className="h-4 w-20 rounded mb-2" />
            <SkeletonLoader className="h-10 w-full rounded" />
          </div>
          <div>
            <SkeletonLoader className="h-4 w-20 rounded mb-2" />
            <SkeletonLoader className="h-10 w-full rounded" />
          </div>
          <div>
            <SkeletonLoader className="h-4 w-16 rounded mb-2" />
            <SkeletonLoader className="h-10 w-full rounded" />
          </div>
          <div>
            <SkeletonLoader className="h-4 w-12 rounded mb-2" />
            <SkeletonLoader className="h-10 w-full rounded" />
          </div>
          <div>
            <SkeletonLoader className="h-4 w-24 rounded mb-2" />
            <SkeletonLoader className="h-10 w-full rounded" />
          </div>
          <div>
            <SkeletonLoader className="h-4 w-16 rounded mb-2" />
            <SkeletonLoader className="h-10 w-full rounded" />
          </div>
          <SkeletonLoader className="h-10 w-16 rounded" />
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-grow flex flex-col">
        {/* Search input */}
        <div className="relative max-w-lg mb-4">
          <SkeletonLoader className="h-10 w-full rounded-lg" />
        </div>

        {/* Roster Table */}
        <div className="flex-grow min-h-0">
      <div className={`overflow-x-auto rounded-lg border relative ${
        isDarkMode
          ? "bg-card-gradient backdrop-blur-sm border-brand-border-logo shadow-lg"
          : "bg-base-100 border-base-200"
      }`}>
        {/* Table Headers */}
        <div className={`px-6 py-3 border-b ${
          isDarkMode ? 'border-brand-border-light' : 'border-gray-200'
        }`}>
          <div className="grid grid-cols-6 gap-4">
            <SkeletonLoader className="h-4 w-4 rounded" />
            <SkeletonLoader className="h-4 w-16 rounded" />
            <SkeletonLoader className="h-4 w-12 rounded" />
            <SkeletonLoader className="h-4 w-12 rounded" />
            <SkeletonLoader className="h-4 w-16 rounded" />
            <SkeletonLoader className="h-4 w-16 rounded" />
          </div>
        </div>
        
        {/* Table Rows */}
        {[...Array(8)].map((_, index) => (
          <div 
            key={index}
            className={`px-6 py-3 ${
              isDarkMode ? 'border-b border-white/10' : 'border-b border-gray-200'
            }`}
          >
            <div className="grid grid-cols-6 gap-4">
              <div className="flex items-center justify-center">
                <SkeletonLoader className="h-4 w-4 rounded" />
              </div>
              <div className="flex items-center gap-3">
                <SkeletonLoader className="h-10 w-10 rounded-full" />
                <div>
                  <SkeletonLoader className="h-4 w-24 rounded mb-1" />
                  <SkeletonLoader className="h-3 w-16 rounded" />
                </div>
              </div>
              <div className="flex items-center">
                <SkeletonLoader className="h-6 w-16 rounded-full" />
              </div>
              <div className="flex items-center">
                <SkeletonLoader className="h-4 w-32 rounded" />
              </div>
              <div className="flex items-center">
                <SkeletonLoader className="h-4 w-4 rounded" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonLoader className="h-8 w-8 rounded" />
                <SkeletonLoader className="h-8 w-8 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className={`flex-shrink-0 flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 p-3 rounded-lg ${
        isDarkMode 
          ? "bg-brand-card-bg/80 backdrop-blur-sm border border-brand-border-light" 
          : "bg-base-100 border border-base-200"
      }`}>
        <div className="flex items-center gap-2">
          <SkeletonLoader className="h-4 w-12 rounded" />
          <SkeletonLoader className="h-8 w-16 rounded" />
          <SkeletonLoader className="h-4 w-16 rounded" />
        </div>
        <SkeletonLoader className="h-4 w-48 rounded" />
        <div className="flex items-center gap-2">
          <SkeletonLoader className="h-8 w-20 rounded" />
          <SkeletonLoader className="h-8 w-8 rounded" />
          <SkeletonLoader className="h-8 w-8 rounded" />
          <SkeletonLoader className="h-8 w-8 rounded" />
          <SkeletonLoader className="h-8 w-16 rounded" />
        </div>
      </div>
    </div>
  );
};

// Skeleton for event detail page
export const SkeletonEventDetail = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  return (
    <div className="space-y-6">
      {/* Event Header */}
      <div className={`rounded-xl overflow-hidden relative ${
        isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
      }`}>
        <div className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-4">
              <SkeletonLoader className="h-16 w-16 rounded-xl" />
            </div>
            <div className="flex-1">
              <SkeletonLoader className="h-7 w-64 rounded mb-2" />
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-1">
                  <SkeletonLoader className="h-4 w-4 rounded" />
                  <SkeletonLoader className="h-4 w-28 rounded" />
                </div>
                <div className="flex items-center space-x-1">
                  <SkeletonLoader className="h-4 w-4 rounded" />
                  <SkeletonLoader className="h-4 w-20 rounded" />
                </div>
              </div>
            </div>
            <SkeletonLoader className="h-10 w-32 rounded-lg" />
          </div>
        </div>
      </div>
      
      {/* Event Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {/* Description */}
          <div className={`rounded-xl ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="p-6">
              <SkeletonLoader className="h-6 w-32 rounded mb-3" />
              <div className="space-y-2">
                <SkeletonLoader className="h-4 w-full rounded" />
                <SkeletonLoader className="h-4 w-full rounded" />
                <SkeletonLoader className="h-4 w-3/4 rounded" />
              </div>
            </div>
          </div>
          
          {/* Schedule */}
          <div className={`rounded-xl ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="p-6">
              <SkeletonLoader className="h-6 w-32 rounded mb-3" />
              <div className="space-y-4">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SkeletonLoader className="h-10 w-10 rounded-lg" />
                    <div className="flex-1">
                      <SkeletonLoader className="h-5 w-48 rounded mb-1" />
                      <SkeletonLoader className="h-4 w-32 rounded" />
                    </div>
                    <SkeletonLoader className="h-6 w-16 rounded" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Registration Info */}
          <div className={`rounded-xl ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="p-6">
              <SkeletonLoader className="h-6 w-40 rounded mb-3" />
              <div className="space-y-3">
                <div className="flex justify-between">
                  <SkeletonLoader className="h-4 w-24 rounded" />
                  <SkeletonLoader className="h-4 w-20 rounded" />
                </div>
                <div className="flex justify-between">
                  <SkeletonLoader className="h-4 w-28 rounded" />
                  <SkeletonLoader className="h-4 w-16 rounded" />
                </div>
                <SkeletonLoader className="h-10 w-full rounded-lg mt-3" />
              </div>
            </div>
          </div>
          
          {/* Location */}
          <div className={`rounded-xl ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="p-6">
              <SkeletonLoader className="h-6 w-24 rounded mb-3" />
              <SkeletonLoader className="h-32 w-full rounded mb-3" />
              <SkeletonLoader className="h-4 w-full rounded mb-2" />
              <SkeletonLoader className="h-4 w-3/4 rounded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Judge Station Skeleton Components

/**
 * Skeleton for StyleSpecificScoringCard
 */
export const StyleSpecificScoringCardSkeleton = ({ className = '' }) => {
  return (
    <div className={`h-full flex flex-col min-h-0 relative ${className}`}>
      {/* Header Skeleton */}
      <div className="flex justify-between items-center mb-1 flex-shrink-0 px-1">
        <div className="flex items-center gap-2">
          <SkeletonLoader className="h-5 w-5 rounded" />
          <SkeletonLoader className="h-6 w-48 rounded" />
        </div>
        <div className="flex items-center gap-2">
          <SkeletonLoader className="h-8 w-16 rounded" />
          <SkeletonLoader className="h-8 w-20 rounded" />
        </div>
      </div>

      {/* Summary Skeleton */}
      <div className="flex gap-2 mb-1 flex-shrink-0 px-1">
        <SkeletonLoader className="h-6 w-20 rounded" />
        <SkeletonLoader className="h-6 w-28 rounded" />
      </div>

      {/* Content Area Skeleton */}
      <div className="flex-1 min-h-0 relative flex flex-col">
        <div className="bg-white/5 border border-white/10 rounded-lg flex-1 min-h-0 flex flex-col">
          <div className="flex-1 overflow-y-auto pb-16">
            <div className="flex gap-3 p-4">
              {/* Multiple columns of skeleton items */}
              {Array.from({ length: 3 }).map((_, columnIndex) => (
                <div key={columnIndex} className="flex-1 flex flex-col min-w-0 space-y-4">
                  {/* Category header */}
                  <div className="backdrop-blur-xl border rounded-lg p-2 bg-purple-500/20 border-purple-400/40">
                    <SkeletonLoader className="h-4 w-24 rounded" />
                  </div>
                  
                  {/* Criteria items */}
                  <div className="space-y-2 px-1">
                    {Array.from({ length: 4 }).map((_, itemIndex) => (
                      <div key={itemIndex} className="p-2 rounded-lg border bg-white/5 border-white/10">
                        <SkeletonLoader className="h-4 w-full rounded" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton for PerformanceSchedule
 */
export const PerformanceScheduleSkeleton = ({ className = '' }) => {
  return (
    <div className={`h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4 px-4 pt-4">
        <div className="flex items-center gap-2">
          <SkeletonLoader className="h-5 w-5 rounded" />
          <SkeletonLoader className="h-6 w-32 rounded" />
        </div>
        <SkeletonLoader className="h-8 w-20 rounded" />
      </div>

      {/* Schedule Items */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="p-3 rounded-lg border bg-white/5 border-white/10">
              <div className="flex justify-between items-center mb-2">
                <SkeletonLoader className="h-5 w-16 rounded" />
                <SkeletonLoader className="h-4 w-12 rounded" />
              </div>
              <SkeletonLoader className="h-4 w-full rounded mb-1" />
              <SkeletonLoader className="h-3 w-3/4 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton for TotalScoreCard
 */
export const TotalScoreCardSkeleton = ({ className = '' }) => {
  return (
    <div className={`h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4 px-4 pt-4">
        <div className="flex items-center gap-2">
          <SkeletonLoader className="h-5 w-5 rounded" />
          <SkeletonLoader className="h-6 w-28 rounded" />
        </div>
      </div>

      {/* Score Input Area */}
      <div className="flex-1 px-4 pb-4">
        <div className="space-y-4">
          {/* Score Input */}
          <div className="space-y-2">
            <SkeletonLoader className="h-4 w-20 rounded" />
            <SkeletonLoader className="h-12 w-full rounded" />
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <SkeletonLoader className="h-4 w-24 rounded" />
            <SkeletonLoader className="h-20 w-full rounded" />
          </div>

          {/* Submit Button */}
          <SkeletonLoader className="h-10 w-full rounded" />
        </div>
      </div>
    </div>
  );
};
